import React, { useCallback, useState } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import {
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Snackbar,
  Stack,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import { useRecoilState } from "recoil";
import PageContent from "../../../components/PageContent";
import PageHeader from "../../../components/PageHeader";
import CircleImage from "../../../components/atoms/CircleImage";
import userDataState from "../../../atoms/userDataAtom";
import UserData, { uploadUserAvatar } from "../../../data/UserData";
import useCoachSignOut from "../../../hooks/useCoachSignOut";
import NewColors from "../../../themes/NewColors";
import { getPasswordResetActionCodeSettings } from "../../../../../../shared/auth/passwordResetActionCodeSettings";
import LockResetIcon from "@mui/icons-material/LockReset";
import LogoutIcon from "@mui/icons-material/Logout";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";

function authErrorMessage(code) {
  switch (code) {
    case "auth/wrong-password":
      return "Incorrect password. Try again.";
    case "auth/weak-password":
      return "Password is too weak.";
    case "auth/requires-recent-login":
      return "Please sign out and sign in again, then retry.";
    case "auth/email-already-in-use":
      return "That email is already used by another account.";
    case "auth/invalid-email":
      return "Enter a valid email address.";
    case "auth/operation-not-allowed":
      return "Email update is not allowed for this account.";
    default:
      return "Something went wrong. Please try again.";
  }
}

const CoachProfilePage = () => {
  const [userData, setUserData] = useRecoilState(userDataState);
  const handleSignOut = useCoachSignOut();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [passwordForEmailChange, setPasswordForEmailChange] = useState("");

  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarProgress, setAvatarProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fileInputRef = React.useRef(null);

  React.useEffect(() => {
    if (!userData) return;
    setFirstName(userData.firstName || "");
    setLastName(userData.lastName || "");
    const u = firebase.auth().currentUser;
    setEmail((u && u.email) || userData.email || "");
  }, [userData]);

  const canonicalInitialEmail = (
    firebase.auth().currentUser?.email ||
    userData?.email ||
    ""
  )
    .trim()
    .toLowerCase();

  const emailChanged =
    email.trim().toLowerCase() !== canonicalInitialEmail;

  const showPasswordField = emailChanged;

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleAvatarFile = useCallback(
    async (e) => {
      const file = e.target?.files?.[0];
      if (!file || !userData?.uid) return;

      const validTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!validTypes.includes(file.type)) {
        showSnackbar("Please select a JPG or PNG image.", "error");
        e.target.value = "";
        return;
      }
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        showSnackbar("Image must be smaller than 5MB.", "error");
        e.target.value = "";
        return;
      }

      try {
        setAvatarUploading(true);
        setAvatarProgress(0);
        await uploadUserAvatar({
          uid: userData.uid,
          file,
          progressFunction: setAvatarProgress,
        });
        const fresh = await UserData.getUserData(userData.uid);
        if (fresh) setUserData(fresh);
        showSnackbar("Profile photo updated.");
      } catch (err) {
        console.error(err);
        showSnackbar("Could not update profile photo.", "error");
      } finally {
        setAvatarUploading(false);
        setAvatarProgress(0);
        e.target.value = "";
      }
    },
    [userData, setUserData, showSnackbar],
  );

  const handleSaveProfile = useCallback(async () => {
    const authUser = firebase.auth().currentUser;
    if (!userData?.uid || !authUser) {
      showSnackbar("You must be signed in to save.", "error");
      return;
    }

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      showSnackbar("Email is required.", "error");
      return;
    }

    const nameUpdates = {};
    if ((userData.firstName || "") !== firstName.trim()) {
      nameUpdates.firstName = firstName.trim();
    }
    if ((userData.lastName || "") !== lastName.trim()) {
      nameUpdates.lastName = lastName.trim();
    }

    if (
      Object.keys(nameUpdates).length === 0 &&
      !emailChanged
    ) {
      showSnackbar("No changes to save.");
      return;
    }

    if (emailChanged && !passwordForEmailChange) {
      showSnackbar(
        "Enter your current password to change your login email.",
        "error",
      );
      return;
    }

    setSaving(true);
    try {
      if (Object.keys(nameUpdates).length > 0) {
        await UserData.updateUser(userData.uid, nameUpdates);
      }

      if (emailChanged) {
        const credential = firebase.auth.EmailAuthProvider.credential(
          authUser.email,
          passwordForEmailChange,
        );
        await authUser.reauthenticateWithCredential(credential);
        await authUser.updateEmail(trimmedEmail);
        await UserData.updateUser(userData.uid, { email: trimmedEmail });
        localStorage.setItem("email", trimmedEmail);
        setPasswordForEmailChange("");
      }

      const fresh = await UserData.getUserData(userData.uid);
      if (fresh) setUserData(fresh);

      showSnackbar("Profile saved.");
    } catch (err) {
      console.error(err);
      const code = err?.code || "";
      showSnackbar(authErrorMessage(code) || err?.message || "Save failed.", "error");
    } finally {
      setSaving(false);
    }
  }, [
    userData,
    firstName,
    lastName,
    email,
    emailChanged,
    passwordForEmailChange,
    setUserData,
    showSnackbar,
  ]);

  const handleSendPasswordReset = useCallback(async () => {
    const addr =
      firebase.auth().currentUser?.email || email.trim();
    if (!addr) {
      showSnackbar("No email address on file.", "error");
      return;
    }
    try {
      await firebase.auth().sendPasswordResetEmail(
        addr,
        getPasswordResetActionCodeSettings(),
      );
      showSnackbar(`Password reset email sent to ${addr}.`);
    } catch (err) {
      console.error(err);
      showSnackbar(
        err?.message || "Could not send reset email.",
        "error",
      );
    }
  }, [email, showSnackbar]);

  if (!userData) {
    return (
      <PageContent
        header={
          <PageHeader title="Profile" subtitle="Manage your account" />
        }
      >
        <Box p={3}>
          <Typography>Loading profile…</Typography>
        </Box>
      </PageContent>
    );
  }

  return (
    <PageContent
      header={
        <PageHeader
          title="Profile"
          subtitle="Personal information, login email, and security"
        />
      }
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          py: 3,
          px: { xs: 2, md: 3 },
          backgroundColor: NewColors.pageBackground,
        }}
      >
        <Stack spacing={3} maxWidth={720} mx="auto">
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Personal information
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your name and email are shown to your organization. Login email
                updates your Firebase sign-in address.
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={3}
                alignItems={{ sm: "center" }}
                sx={{ mb: 3 }}
              >
                <Box sx={{ position: "relative" }}>
                  <CircleImage
                    firstName={firstName}
                    lastName={lastName}
                    size={96}
                    url={userData.avatarUrl || null}
                  />
                </Box>
                <Stack spacing={1}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<PhotoCameraIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={avatarUploading}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    Change photo
                  </Button>
                  <input
                    ref={fileInputRef}
                    hidden
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleAvatarFile}
                  />
                  {avatarUploading && (
                    <LinearProgress
                      variant="determinate"
                      value={avatarProgress}
                      sx={{ maxWidth: 240, borderRadius: 1, height: 6 }}
                    />
                  )}
                </Stack>
              </Stack>

              <Stack spacing={2}>
                <TextField
                  label="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  fullWidth
                  autoComplete="given-name"
                />
                <TextField
                  label="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  fullWidth
                  autoComplete="family-name"
                />
                <TextField
                  label="Login email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  autoComplete="email"
                  helperText={
                    emailChanged
                      ? "Enter your current password below to confirm this change."
                      : undefined
                  }
                />
                {showPasswordField && (
                  <TextField
                    label="Current password"
                    type="password"
                    value={passwordForEmailChange}
                    onChange={(e) => setPasswordForEmailChange(e.target.value)}
                    fullWidth
                    autoComplete="current-password"
                  />
                )}
                <Box>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveProfile}
                    disabled={saving || avatarUploading}
                    sx={{ textTransform: "none", fontWeight: 600, mt: 1 }}
                  >
                    {saving ? "Saving…" : "Save changes"}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Security
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Reset your password via email, or sign out on this device.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<LockResetIcon />}
                  onClick={handleSendPasswordReset}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Send password reset email
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<LogoutIcon />}
                  onClick={() => handleSignOut("Profile page")}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Sign out
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContent>
  );
};

export default CoachProfilePage;
